/* Plans that propose a correction, what each would change, and why it may not proceed.
 *
 * **Two sentences sit above the table and both are load-bearing.**
 *
 * *This deployment cannot execute a plan* is a fact about the deployment rather than about any row:
 * no write adapter exists, so an approved plan emits a script somebody's DBA runs. There is no
 * Execute control anywhere on this screen, and its absence is the honest answer rather than a
 * button that would fail on click.
 *
 * *Flagged is not fixed* is the product's central promise. Lumiq never invents a value, so a finding
 * whose correct value is not derivable is recorded and never applied — and a fix-shaped screen that
 * showed only the correctable half would let a steward believe a plan covering 3 of 40 findings had
 * covered them.
 *
 * **An unsimulated plan reads "not simulated", never zero.** Simulated-and-found-nothing is a
 * different fact, and a confident zero would let an approver sign over counts nobody has taken.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Plan } from '@/shared/api/generated/remediation';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { usePlanQueue } from '../api/plans';
import {
  asInstant,
  identityOf,
  matchesMode,
  matchesState,
  MODE_FILTERS,
  rowsReading,
  stateLabelKey,
  STATE_FILTERS,
  stateToneOf,
  unfixableCount,
  waitingOnAPerson,
} from '../model/plans';
import type { ModeFilter, StateFilter } from '../model/plans';

function PlanRow({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = (): void => {
    navigate(`/remediation/${encodeURIComponent(plan.id)}`);
  };

  const flagged = unfixableCount(plan);
  const rows = rowsReading(plan);
  const identity = identityOf(plan);

  return (
    <tr className="lq-rm__row" onClick={open}>
      <td>
        {/* **The table names the subject; the instant names the row.** Several plans against one
            table is the ordinary case, so the name alone left three rows reading `PUBLIC.FACT_SALES`
            and nothing to choose between them. Which of the plan's fields does the choosing is
            `identityOf`'s judgement rather than this component's. The id is shortened rather than
            dropped, and its full 36 characters stay on `title` and on the detail page. */}
        <strong className="lq-mono lq-rm__relation">
          {identity.relation ?? t('remediation.unnamedRelation')}
        </strong>
        <div className="lq-rm__made">
          {t('remediation.made', { when: asInstant(identity.madeAt) })}
        </div>
        <div className="lq-cell__secondary lq-mono lq-rm__id" title={identity.id}>
          {identity.shortId}
        </div>
      </td>
      <td>
        <span
          className={`lq-tag lq-tag--rm-${stateToneOf(plan.state)}`}
          title={t(`remediation.stateHint.${plan.state}`)}
        >
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(stateLabelKey(plan.state))}
        </span>
      </td>
      <td className="lq-nowrap">
        {t(`remediation.mode.${plan.mode}`)}
        <div className="lq-cell__secondary">{t(`remediation.modeNote.${plan.mode}`)}</div>
      </td>
      <td className="lq-nowrap">
        {t('remediation.correctable', { count: plan.applicable_count })}
        {/* Warn-toned only when there is something to report. Amber on every row, including the
            rows where nothing was flagged, is how a reader learns to stop seeing the colour. */}
        <div className={flagged > 0 ? 'lq-rm__flagged' : 'lq-cell__secondary'}>
          {t('remediation.flagged', { count: flagged })}
        </div>
      </td>
      <td className="lq-num lq-nowrap">
        {rows.state === 'unsimulated' ? (
          <span className="lq-muted">{t('remediation.notSimulated')}</span>
        ) : (
          rows.rows.toLocaleString()
        )}
        <div className="lq-cell__secondary">
          {t(
            rows.state === 'unsimulated'
              ? 'remediation.rowsNote.unsimulated'
              : 'remediation.rowsNote.measured',
          )}
        </div>
      </td>
      <td className="lq-nowrap lq-cell--quiet">
        {plan.decided_by ?? t('remediation.undecided')}
        <div className="lq-cell__secondary">
          {plan.decided_at === null ? '' : asInstant(plan.decided_at)}
        </div>
      </td>
      <td className="lq-table__actions">
        <button
          type="button"
          className="lq-rowaction"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t('remediation.open')}
        </button>
      </td>
    </tr>
  );
}

export function RemediationQueue(): ReactElement {
  const { t } = useTranslation();
  const queue = usePlanQueue();
  const [state, setState] = useState<StateFilter>('all');
  const [mode, setMode] = useState<ModeFilter>('all');

  const all = queue.isSuccess ? queue.data : [];
  const visible = all.filter(
    (plan) => matchesState(plan, state) && matchesMode(plan, mode),
  );
  const filtering = state !== 'all' || mode !== 'all';

  return (
    <div className="lq-rm lq-refscreen">
      <PageHeader
        title={t('remediation.title')}
        lede={t('remediation.intro')}
        meta={
          queue.isSuccess ? (
            <>
              <div>{t('remediation.meta.count', { count: all.length })}</div>
              <div>{t('remediation.meta.waiting', { count: waitingOnAPerson(all) })}</div>
            </>
          ) : undefined
        }
      />
      <div className="lq-pagerule" />

      {/* First, and about the deployment rather than about anything below it. */}
      <div className="lq-band lq-band--rm-warn">
        <div className="lq-band__title">{t('remediation.noExecute.title')}</div>
        <div className="lq-band__body">{t('remediation.noExecute.body')}</div>
      </div>
      <div className="lq-band lq-band--info lq-band--closing">
        <div className="lq-band__title">{t('remediation.neverInvents.title')}</div>
        <div className="lq-band__body">{t('remediation.neverInvents.body')}</div>
      </div>

      {queue.isPending && <SkeletonRows rows={4} columns={7} />}

      {queue.isError && (
        <Alert tone="error" title={t('remediation.loadFailed')}>
          {queue.error.message}
        </Alert>
      )}

      {queue.isSuccess && (
        <>
          <div className="lq-rm__filters">
            <span className="lq-rm__filterLabel">{t('remediation.filter.state')}</span>
            {STATE_FILTERS.map((each) => (
              <button
                key={each}
                type="button"
                className={
                  each === state ? 'lq-rm__filter lq-rm__filter--on' : 'lq-rm__filter'
                }
                aria-pressed={each === state}
                onClick={() => {
                  setState(each);
                }}
              >
                {t(`remediation.filter.${each}`)}
              </button>
            ))}
            <span className="lq-rm__filterLabel lq-rm__filterLabel--spaced">
              {t('remediation.filter.mode')}
            </span>
            <select
              className="lq-rm__select"
              aria-label={t('remediation.filter.mode')}
              value={mode}
              onChange={(event) => {
                setMode(event.target.value as ModeFilter);
              }}
            >
              {MODE_FILTERS.map((each) => (
                <option key={each} value={each}>
                  {t(`remediation.modeFilter.${each}`)}
                </option>
              ))}
            </select>
          </div>

          {visible.length === 0 ? (
            /* Two different sentences. An empty estate is a fact about the tenant; an empty filter
               is a fact about the filter, and confusing them would report a queue with work in it
               as an empty one. */
            <div className="lq-cp__none">
              <div className="lq-cp__noneTitle">
                {t(filtering ? 'remediation.filtered.title' : 'remediation.empty.title')}
              </div>
              <div className="lq-cp__noneBody">
                {t(filtering ? 'remediation.filtered.body' : 'remediation.empty.body')}
              </div>
            </div>
          ) : (
            <>
              <div className="lq-table-scroll lq-table-scroll--framed">
                <table className="lq-table lq-table--ledger">
                  <caption className="lq-visually-hidden">
                    {t('remediation.caption')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('remediation.column.plan')}</th>
                      <th scope="col">{t('remediation.column.state')}</th>
                      <th scope="col">{t('remediation.column.mode')}</th>
                      <th scope="col">{t('remediation.column.findings')}</th>
                      <th scope="col" className="lq-num">
                        {t('remediation.column.rows')}
                      </th>
                      <th scope="col">{t('remediation.column.decided')}</th>
                      <th scope="col" className="lq-table__actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((plan) => (
                      <PlanRow key={plan.id} plan={plan} />
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="lq-note">{t('remediation.queueNote')}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
